/**
 * Feature packs (WASM binaries, AI models) live in their own Cache Storage bucket so the
 * service worker never precaches them and the settings screen can evict them one by one.
 */
export const MODULE_CACHE_NAME = "mediaknife-modules-v1";

export type DownloadProgress = {
  readonly receivedBytes: number;
  /** null when the server sends no content-length. */
  readonly totalBytes: number | null;
};

export type FetchOptions = {
  readonly signal?: AbortSignal;
  readonly onProgress?: (progress: DownloadProgress) => void;
  readonly cacheName?: string;
};

export class ModuleDownloadError extends Error {
  constructor(url: string, cause: string) {
    super(`モジュールを取得できませんでした (${url}): ${cause}`);
    this.name = "ModuleDownloadError";
  }
}

function countingStream(
  body: ReadableStream<Uint8Array>,
  totalBytes: number | null,
  onProgress: (progress: DownloadProgress) => void,
): ReadableStream<Uint8Array> {
  let receivedBytes = 0;
  return body.pipeThrough(
    new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        receivedBytes += chunk.byteLength;
        onProgress({ receivedBytes, totalBytes });
        controller.enqueue(chunk);
      },
    }),
  );
}

/**
 * Returns the cached response when present, otherwise streams the download straight into
 * the cache. The body is never buffered whole, so a 150 MB model costs no extra memory.
 */
export async function fetchModule(url: string, options: FetchOptions = {}): Promise<Response> {
  const cacheName = options.cacheName ?? MODULE_CACHE_NAME;
  const cache = await caches.open(cacheName);

  const cached = await cache.match(url);
  if (cached) return cached;

  const response = await fetch(url, options.signal ? { signal: options.signal } : {});
  if (!response.ok) throw new ModuleDownloadError(url, `HTTP ${response.status}`);
  if (!response.body) throw new ModuleDownloadError(url, "本文がありません");

  const declared = Number(response.headers.get("content-length"));
  const totalBytes = Number.isFinite(declared) && declared > 0 ? declared : null;
  const body = options.onProgress
    ? countingStream(response.body, totalBytes, options.onProgress)
    : response.body;

  await cache.put(url, new Response(body, { headers: response.headers }));

  const stored = await cache.match(url);
  if (!stored) throw new ModuleDownloadError(url, "キャッシュへの保存に失敗しました");
  return stored;
}

export async function cachedModuleUrls(cacheName = MODULE_CACHE_NAME): Promise<string[]> {
  const cache = await caches.open(cacheName);
  return (await cache.keys()).map((request) => request.url);
}

export async function evictModule(url: string, cacheName = MODULE_CACHE_NAME): Promise<boolean> {
  const cache = await caches.open(cacheName);
  return cache.delete(url);
}

export async function cachedModuleSizeBytes(cacheName = MODULE_CACHE_NAME): Promise<number> {
  const cache = await caches.open(cacheName);
  const requests = await cache.keys();
  const sizes = await Promise.all(
    requests.map(async (request) => {
      const response = await cache.match(request);
      if (!response) return 0;
      return (await response.clone().blob()).size;
    }),
  );
  return sizes.reduce((total, size) => total + size, 0);
}
